import { AudioElement } from '@src/editor/models/elements/AudioElement';
import { KaraokeClip } from '@src/editor/models/karaoke/KaraokeClip';
import { TranscriptTrack } from '@src/editor/models/TranscriptTrack';

import type { ReelSection } from './deriveReelSections';
import type { Element } from '@src/editor/models/Element';
import type { Timeline } from '@src/editor/models/Timeline';

export type ReelSectionContentsOptions = {
  /**
   * Reel-level template elements (intro / end cards, logos) to leave behind.
   * They are pinned to the reel's own start or end rather than to a passage, so
   * they are not any Section's to carry (DE7). Which elements those are lives
   * in the video template settings, which is store state — so the caller
   * supplies them rather than this function reaching for a store.
   */
  excludeElementIds?: ReadonlySet<string>;
};

/**
 * PLAT-11050 — the elements a Section owns, and therefore the elements that
 * travel with it when it moves or is removed (DE7).
 *
 * **Ownership rule: an element belongs to the Section that contains its start.**
 * Total and unambiguous — ownership by overlap would leave an element that
 * straddles a boundary owned by two Sections and force a split decision on
 * every drag.
 *
 * Excluded by construction:
 * - the transcript track's own elements, which *are* the passage rather than
 *   something sitting on top of it;
 * - audio, because music spans the whole reel;
 * - captions, for the transcript track's reason one step removed: a
 *   {@link KaraokeClip} takes its position from the first transcript token it
 *   holds, so it is not *at* a time that a caller could carry it to — `time` is
 *   a computed getter, and assigning to it throws (PLAT-11191). Captions follow
 *   their words on their own once the transcript is re-projected;
 * - anything in `excludeElementIds`.
 */
export function reelSectionContents(
  timeline: Timeline,
  section: ReelSection,
  options: ReelSectionContentsOptions = {},
): Element[] {
  const excluded = options.excludeElementIds;

  const transcriptElementIds = new Set(
    timeline.tracks
      .filter(
        (track): track is TranscriptTrack => track instanceof TranscriptTrack,
      )
      .flatMap(track => track.elements.map(element => element.id)),
  );

  return timeline.allElements.filter(element => {
    if (transcriptElementIds.has(element.id)) return false;
    if (element instanceof AudioElement) return false;
    if (element instanceof KaraokeClip) return false;
    if (excluded?.has(element.id)) return false;

    return element.time >= section.startTime && element.time < section.endTime;
  });
}
